import { useEffect, useRef, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { RoleOption, StatusOption, OrgFilterOption } from "../../../../store/slices/GlobalSearchSlice";

interface SearchFiltersProps {
    showRoleFilter: boolean;
    showStatusFilter: boolean;
    selectedRole: string;
    selectedStatus: string;
    selectedOrgs: string[];
    roleOptions: RoleOption[];
    statusOptions: StatusOption[];
    orgFilterOptions: OrgFilterOption[];
    onRoleChange: (role: string) => void;
    onStatusChange: (status: string) => void;
    onOrgToggle: (orgId: string) => void;
    onOrgClearAll: () => void;
}

const SearchFilters = ({
    showRoleFilter,
    showStatusFilter,
    selectedRole,
    selectedStatus,
    selectedOrgs,
    roleOptions,
    statusOptions,
    orgFilterOptions,
    onRoleChange,
    onStatusChange,
    onOrgToggle,
    onOrgClearAll,
}: SearchFiltersProps) => {
    const [roleOpen, setRoleOpen] = useState(false);
    const [statusOpen, setStatusOpen] = useState(false);
    const [orgOpen, setOrgOpen] = useState(false);

    const roleRef = useRef<HTMLDivElement>(null);
    const statusRef = useRef<HTMLDivElement>(null);
    const orgRef = useRef<HTMLDivElement>(null);

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (roleRef.current && !roleRef.current.contains(target)) {
                setRoleOpen(false);
            }
            if (statusRef.current && !statusRef.current.contains(target)) {
                setStatusOpen(false);
            }
            if (orgRef.current && !orgRef.current.contains(target)) {
                setOrgOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Flatten all org options for the flat multi-select list
    const allOrgOptions = orgFilterOptions.flatMap((org) => [
        { id: org.id, displayName: org.displayName },
        ...org.subOrgs.map((sub) => ({ id: sub.orgId, displayName: sub.displayName })),
    ]);

    const getOrgFilterLabel = () => {
        if (selectedOrgs.length === 0) return "Organization";
        if (selectedOrgs.length === 1) {
            const found = allOrgOptions.find((o) => o.id === selectedOrgs[0]);
            return found?.displayName || "Organization";
        }
        return `${selectedOrgs.length} selected`;
    };

    return (
        <div className="flex items-center gap-3">
            {/* Role Filter */}
            {showRoleFilter && (
                <div className="relative" ref={roleRef}>
                    <button
                        type="button"
                        onClick={() => { setRoleOpen(!roleOpen); setStatusOpen(false); setOrgOpen(false); }}
                        className="flex items-center gap-1 px-3 py-2 border border-brColor rounded-lg text-sm font-semibold text-textColor3 shadow-light-sm hover:bg-gray-50"
                    >
                        {selectedRole === "ALL" ? "Role" : roleOptions.find((r) => r.role === selectedRole)?.displayName || "Role"}
                        <ChevronDownIcon className="w-5 h-5" />
                    </button>
                    {roleOpen && (
                        <div className="absolute right-0 mt-1 w-48 bg-white border border-shadowborder rounded-lg shadow-lg z-20">
                            {roleOptions.map((opt) => (
                                <button
                                    key={opt.role}
                                    type="button"
                                    onClick={() => { onRoleChange(opt.role); setRoleOpen(false); }}
                                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                                        selectedRole === opt.role ? "bg-gray-100 font-medium" : ""
                                    }`}
                                >
                                    {opt.displayName}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Status Filter */}
            {showStatusFilter && (
                <div className="relative" ref={statusRef}>
                    <button
                        type="button"
                        onClick={() => { setStatusOpen(!statusOpen); setRoleOpen(false); setOrgOpen(false); }}
                        className="flex items-center gap-1 px-3 py-2 border border-brColor rounded-lg text-sm font-semibold text-textColor3 shadow-light-sm hover:bg-gray-50"
                    >
                        {selectedStatus === "ALL" ? "Status" : statusOptions.find((s) => s.status === selectedStatus)?.displayName || "Status"}
                        <ChevronDownIcon className="w-5 h-5" />
                    </button>
                    {statusOpen && (
                        <div className="absolute right-0 mt-1 w-48 bg-white border border-shadowborder rounded-lg shadow-lg z-20">
                            {statusOptions.map((opt) => (
                                <button
                                    key={opt.status}
                                    type="button"
                                    onClick={() => { onStatusChange(opt.status); setStatusOpen(false); }}
                                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                                        selectedStatus === opt.status ? "bg-gray-100 font-medium" : ""
                                    }`}
                                >
                                    {opt.displayName}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Organization Filter (multi-select, flat list) */}
            <div className="relative" ref={orgRef}>
                <button
                    type="button"
                    onClick={() => { setOrgOpen(!orgOpen); setRoleOpen(false); setStatusOpen(false); }}
                    className="flex items-center gap-1 px-3 py-2 border border-brColor rounded-lg text-sm font-semibold text-textColor3 shadow-light-sm hover:bg-gray-50"
                >
                    {getOrgFilterLabel()}
                    <ChevronDownIcon className="w-5 h-5" />
                </button>
                {orgOpen && (
                    <div className="absolute right-0 mt-1 w-56 bg-white border border-shadowborder rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
                        {selectedOrgs.length > 0 && (
                            <button
                                type="button"
                                onClick={onOrgClearAll}
                                className="block w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-gray-50 border-b border-shadowborder"
                            >
                                Clear all
                            </button>
                        )}
                        {allOrgOptions.map((opt) => (
                            <label
                                key={opt.id}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-gray-50 cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedOrgs.includes(opt.id)}
                                    onChange={() => onOrgToggle(opt.id)}
                                    className="w-4 h-4 rounded border-gray-300 text-green-500 focus:ring-green-500"
                                />
                                <span>{opt.displayName}</span>
                            </label>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchFilters;
